import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Block as BlockIcon } from "@mui/icons-material";

import { usePageTitle } from "../context/PageTitleContext";
import { useStoreDenials } from "../hooks/useDistributionAnalytics";

const STATUS_COLOR: Record<string, "default" | "warning" | "info" | "success" | "error"> = {
  pending_purchase: "warning",
  purchased: "info",
  fulfilled: "success",
  expired: "error",
};

/**
 * B3.34 — DenegacionesPage
 *
 * Lista denegaciones por tienda con estado, antigüedad, PO vinculada.
 * Query param `store` en query string o input manual.
 */
export function DenegacionesPage(): React.ReactElement {
  const { setTitle } = usePageTitle();
  const [store, setStore] = useState("");
  const { data, isLoading } = useStoreDenials(store || undefined);

  useEffect(() => {
    setTitle("Distribución — Denegaciones");
    return () => setTitle("");
  }, [setTitle]);

  const items = (data as Awaited<
    ReturnType<typeof import("../services/distributionService").distributionService.getStoreDenials>
  >) ?? [];

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <BlockIcon color="primary" />
          <Typography variant="h4" component="h1" fontWeight={600}>
            Denegaciones por tienda
          </Typography>
        </Stack>

        <TextField
          label="Tienda (mxp_code)"
          size="small"
          value={store}
          onChange={(e) => setStore(e.target.value.toUpperCase())}
          placeholder="SLP04"
          helperText="Ingresa el código de la tienda para ver sus denegaciones"
        />

        {!store && (
          <Alert severity="info">
            Ingresa un código de tienda (ej. SLP04) para ver sus denegaciones
            activas.
          </Alert>
        )}

        {store && (
          <Paper variant="outlined">
            {isLoading && (
              <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            )}
            {!isLoading && items.length === 0 && (
              <Alert severity="success" sx={{ m: 2 }}>
                {store}: sin denegaciones activas.
              </Alert>
            )}
            {items.length > 0 && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Producto</TableCell>
                      <TableCell align="right">Qty denegada</TableCell>
                      <TableCell align="right">Qty pedida</TableCell>
                      <TableCell>Fuente</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Antigüedad (d)</TableCell>
                      <TableCell>Válida</TableCell>
                      <TableCell>Fecha</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((d) => (
                      <TableRow key={d.id} hover>
                        <TableCell sx={{ fontFamily: "monospace" }}>
                          {d.unified_product_id}
                        </TableCell>
                        <TableCell align="right">{d.qty_denied}</TableCell>
                        <TableCell align="right">{d.qty_requested}</TableCell>
                        <TableCell>{d.source_type}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={STATUS_COLOR[d.status] ?? "default"}
                            label={d.status}
                          />
                        </TableCell>
                        <TableCell align="right">{d.age_days}</TableCell>
                        <TableCell>
                          {d.is_valid ? (
                            <Chip size="small" color="success" label="sí" />
                          ) : (
                            <Chip
                              size="small"
                              color="warning"
                              label={d.invalid_reason ?? "no"}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {new Date(d.denial_date).toLocaleDateString("es-MX")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}
      </Stack>
    </Box>
  );
}
